import React, { useCallback } from "react"
import { Accordion as BSAccordion } from "react-bootstrap"
import { AccordionEventKey } from "react-bootstrap/esm/AccordionContext"

import isEqual from "lodash-es/isEqual"

import { useControlledState } from "shared/lib/useControlledState"
import { useEvent } from "shared/lib/useEvent"
import { AccordionProps } from "shared/types/Accordion"

import { AccordionItem } from "./AccordionItem"

function AccordionComponent<
  T = void,
  P = void,
  ElementType extends HTMLElement = HTMLDivElement
>({
  tabs,
  activeKey,
  ButtonRenderer,
  payload,
  selectedTabs,
  isDisabled,
  defaultActiveKey,
  onSelect,
  onExpand,
  ...rest
}: AccordionProps<T, P, ElementType>) {
  const [internalActiveKey, setInternalActiveKey] =
    useControlledState<AccordionEventKey>({
      value: activeKey,
      onChange: onExpand,
      defaultValue: defaultActiveKey
    })

  const handleExpand = useEvent((nextActiveKey: AccordionEventKey) => {
    if (isEqual(internalActiveKey, nextActiveKey)) {
      setInternalActiveKey(null)
    } else {
      setInternalActiveKey(nextActiveKey)
    }
  })

  const isExpanded = useCallback(
    (key: string) =>
      Array.isArray(internalActiveKey)
        ? internalActiveKey.includes(key)
        : internalActiveKey === key,
    [internalActiveKey]
  )

  return (
    <BSAccordion
      flush
      activeKey={internalActiveKey}
      onSelect={handleExpand}
      {...rest}
    >
      {tabs.map(tab => (
        <AccordionItem
          key={tab.key}
          tab={tab}
          ButtonRenderer={ButtonRenderer}
          payload={payload}
          eventKey={tab.key}
          isExpanded={isExpanded(tab.key)}
          isSelected={selectedTabs?.includes(tab.key)}
          onSelect={onSelect}
          onExpand={handleExpand}
          isDisabled={isDisabled}
        />
      ))}
    </BSAccordion>
  )
}

export const Accordion = React.memo(
  AccordionComponent
) as typeof AccordionComponent
